import { useEffect, useRef } from 'react';
import { canNotify } from './browserNotifications';

/**
 * NOTIF1 N31 (teacher + family): shows a browser notification for each NEW feed row with `browser = true`
 * (recipient ≠ actor, N30). Rows present at the first load are the baseline, never shown.
 * `items` newest first; `toMessage(item)` → {title, body}; `onOpen(item)` on click.
 * `tag` = notification id: several open tabs replace the same OS notification instead of stacking.
 */
export default function useBrowserNotifications({ items, storageKey, toMessage, onOpen, scope }) {
  const seenRef = useRef(null);
  const scopeRef = useRef(scope);
  const handlersRef = useRef({ toMessage, onOpen });
  handlersRef.current = { toMessage, onOpen };

  useEffect(() => {
    if (scopeRef.current !== scope) {
      seenRef.current = null;
      scopeRef.current = scope;
    }
    if (!items) return;
    const ids = items.map((item) => item.notification_id);
    if (seenRef.current === null) {
      seenRef.current = Math.max(0, ...ids);
      return;
    }
    const fresh = items.filter((item) => item.notification_id > seenRef.current);
    if (!fresh.length) return;
    seenRef.current = Math.max(seenRef.current, ...ids);
    if (!canNotify(storageKey)) return;
    fresh.filter((item) => item.browser).forEach((item) => {
      const { title, body } = handlersRef.current.toMessage(item);
      const notification = new window.Notification(title, { body, tag: String(item.notification_id) });
      notification.onclick = () => {
        window.focus();
        handlersRef.current.onOpen(item);
        notification.close();
      };
    });
  }, [items, storageKey, scope]);
}
